import { Channel } from '../Channel';
import { RoleType } from '../Field';
export var LayerMarkType;
(function (LayerMarkType) {
    LayerMarkType["STAR"] = "*";
    LayerMarkType["POINT"] = "POINT";
    LayerMarkType["LINE"] = "LINE";
    LayerMarkType["AREA"] = "AREA";
})(LayerMarkType || (LayerMarkType = {}));
export var PivotSortOrder;
(function (PivotSortOrder) {
    PivotSortOrder["ANY"] = "ANY";
    PivotSortOrder["ROW"] = "ROW";
    PivotSortOrder["COLUMN"] = "COLUMN";
})(PivotSortOrder || (PivotSortOrder = {}));
export class PivotQuery {
    constructor() {
        this._sortOrder = PivotSortOrder.ANY;
        this._rows = new RowAxis(this);
        this._columns = new ColumnAxis(this);
        this._values = new ValueAxis(this);
        this._layers = new Map();
        this.layers.set(Channel.STAR, new Array());
        this.layers.set(Channel.ROW, new Array());
        this.layers.set(Channel.COLUMN, new Array());
        this._sorts = new Map();
        this._filters = new Map();
    }
    get rows() {
        return this._rows;
    }
    get columns() {
        return this._columns;
    }
    get values() {
        return this._values;
    }
    get layers() {
        return this._layers;
    }
    get filters() {
        return this._filters;
    }
    get sorts() {
        return this._sorts;
    }
    get sortOrder() {
        return this._sortOrder;
    }
    onRows(callback) {
        if (callback != null) {
            callback(this.rows);
        }
        return this;
    }
    onColumns(callback) {
        if (callback != null) {
            callback(this.columns);
        }
        return this;
    }
    onValues(callback) {
        if (callback != null) {
            callback(this.values);
        }
        return this;
    }
    onLayer(callback) {
        const layers = this.layers.get(Channel.STAR);
        if (layers.length === 0) {
            layers.push(new LayerAxis(this));
        }
        if (callback != null) {
            callback(layers[0]);
        }
        return this;
    }
    filterBy(...filters) {
        filters.forEach((filter) => this.filters.set(filter.field.name, filter));
        return this;
    }
    sortBy(...sorts) {
        sorts.forEach((sort) => this.sorts.set(sort.field.name, sort));
        return this;
    }
    sortOrderBy(sortOrder) {
        this._sortOrder = sortOrder;
        return this;
    }
}
export class QueryAxis {
    constructor(query) {
        this._query = query;
    }
    get query() {
        return this._query;
    }
}
export class BaseAxis extends QueryAxis {
    constructor(query) {
        super(query);
        this._fields = new Array();
        this._layers = new Map();
    }
    get fields() {
        return this._fields;
    }
    get layers() {
        return this._layers;
    }
    get dimensions() {
        return this.fields.filter((f) => f.isDimension);
    }
    get measures() {
        return this.fields.filter((f) => f.isMeasure);
    }
    onField(...field) {
        field.forEach((f) => this.fields.push(f));
        return this;
    }
    onLayer(field, callback) {
        let layer = this.layers.get(field.alias);
        if (layer == null) {
            layer = new LayerAxis(this.query, this.channel, field.alias);
            this.query.layers.get(this.channel).push(layer);
            this.layers.set(field.alias, layer);
        }
        layer.onChannel(this.channel, field);
        if (callback != null) {
            callback(layer);
        }
        return this;
    }
}
export class RowAxis extends BaseAxis {
    get channel() {
        return Channel.ROW;
    }
}
export class ColumnAxis extends BaseAxis {
    get channel() {
        return Channel.COLUMN;
    }
}
export class ValueAxis extends QueryAxis {
    constructor(query) {
        super(query);
        this._fields = new Array();
    }
    get channel() {
        return Channel.VALUE;
    }
    get fields() {
        return this._fields;
    }
    onField(...field) {
        field.forEach((f) => this.fields.push(f));
        return this;
    }
}
export class LayerAxis extends QueryAxis {
    constructor(query, channel = Channel.STAR, name = '*') {
        super(query);
        this._channel = channel;
        this._name = name;
        this._markType = LayerMarkType.POINT;
        this._fields = new Map();
        this.fields.set(Channel.ROW, new Array());
        this.fields.set(Channel.COLUMN, new Array());
        this.fields.set(Channel.VALUE, new Array());
        this.fields.set(Channel.COLOR, new Array());
        this.fields.set(Channel.SIZE, new Array());
        this.fields.set(Channel.TEXT, new Array());
        this.fields.set(Channel.DETAIL, new Array());
        this.fields.set(Channel.TOOLTIP, new Array());
        this.fields.set(Channel.ANGLE, new Array());
        this.fields.set(Channel.PATH, new Array());
    }
    get name() {
        return this._name;
    }
    get channel() {
        return this._channel;
    }
    get markType() {
        return this._markType;
    }
    get fields() {
        return this._fields;
    }
    get dimensions() {
        return this.getFieldsByRole(RoleType.DIMENSION);
    }
    get measures() {
        return this.getFieldsByRole(RoleType.MEASURE);
    }
    onRow(...field) {
        return this.onChannel(Channel.ROW, ...field);
    }
    onColumn(...field) {
        return this.onChannel(Channel.COLUMN, ...field);
    }
    onValue(...field) {
        return this.onChannel(Channel.VALUE, ...field);
    }
    onColor(...field) {
        return this.onChannel(Channel.COLOR, ...field);
    }
    onSize(...field) {
        return this.onChannel(Channel.SIZE, ...field);
    }
    onText(...field) {
        return this.onChannel(Channel.TEXT, ...field);
    }
    onDetail(...field) {
        return this.onChannel(Channel.DETAIL, ...field);
    }
    onTooltip(...field) {
        return this.onChannel(Channel.TOOLTIP, ...field);
    }
    onChannel(channel, ...field) {
        const channelFields = this.fields.get(channel);
        field.forEach((f) => channelFields.push(f));
        return this;
    }
    getFieldsByRole(roleType) {
        const results = new Map();
        let currField = null;
        for (const [channel, fields] of Array.from(this.fields)) {
            for (let i = 0, len = fields.length; i < len; i++) {
                currField = fields[i];
                if (currField.roleType === roleType) {
                    if (!results.has(channel)) {
                        results.set(channel, new Array());
                    }
                    results.get(channel).push(currField);
                }
            }
        }
        return results;
    }
}
